import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, MagnifyingGlass, UserPlus, X, UploadSimple, MedalMilitary } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { organizationAPI } from '../../utils/api';

export const OrgMembersSection = ({ organization, orgMembers, onRemoveMember, onMemberAdded }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const csvRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search) return orgMembers;
    const q = search.toLowerCase();
    return orgMembers.filter(m =>
      m.rider_name.toLowerCase().includes(q) ||
      (m.rider_email && m.rider_email.toLowerCase().includes(q))
    );
  }, [orgMembers, search]);

  const handleAddMember = async () => {
    if (!addEmail.trim()) { toast.error('Email is required'); return; }
    setAdding(true);
    try {
      const { data } = await organizationAPI.addMember(organization.id, addEmail.trim());
      toast.success(`${data.rider_name || 'Member'} added`);
      setAddEmail('');
      setShowAddForm(false);
      if (onMemberAdded) onMemberAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmRemove = () => {
    if (!removeConfirm) return;
    onRemoveMember(removeConfirm.id);
    setRemoveConfirm(null);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { data } = await organizationAPI.bulkUploadMembers(organization.id, file);
      toast.success(`${data.added} member${data.added !== 1 ? 's' : ''} added${data.skipped ? `, ${data.skipped} skipped (duplicates)` : ''}`);
      if (data.errors?.length > 0) toast.error(`${data.errors.length} row${data.errors.length !== 1 ? 's' : ''} had errors`);
      if (onMemberAdded) onMemberAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV upload failed');
    }
    e.target.value = '';
  };

  return (
    <div className="mb-8" data-testid="org-members-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-white" style={{fontFamily: 'Outfit'}}>
          Organization Members <span className="text-[#9E9E9E] text-sm font-normal">({orgMembers.length})</span>
        </h2>
        <div className="flex items-center gap-2 self-start">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#0096FF]/10 border border-[#0096FF]/30 text-[#0096FF] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#0096FF]/20 transition-colors"
            data-testid="add-member-btn"
          >
            <UserPlus size={16} weight="bold" /> Add Member
          </button>
          <button
            onClick={() => csvRef.current?.click()}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#9E9E9E]/10 border border-[#9E9E9E]/20 text-[#9E9E9E] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#9E9E9E]/20 transition-colors"
            data-testid="csv-upload-members-btn"
          >
            <UploadSimple size={16} weight="bold" /> CSV Upload
          </button>
          <input ref={csvRef} type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" data-testid="csv-member-file-input" />
          <button
            onClick={() => navigate(`/organizations/${organization.id}/points`)}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#FF9500]/20 transition-colors"
            data-testid="view-points-admin-btn"
          >
            <MedalMilitary size={16} weight="fill" /> Points &amp; Standings
          </button>
          <button
            onClick={() => navigate(`/organizations/${organization.id}/leaderboard`)}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#FFD700]/20 transition-colors"
            data-testid="view-leaderboard-btn"
          >
            <Trophy size={16} weight="fill" /> Leaderboard
          </button>
        </div>
      </div>

      {/* Add Member Form */}
      {showAddForm && (
        <div className="mb-4 bg-[#161616] border border-[#0096FF]/20 rounded-lg p-4" data-testid="add-member-form">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-[#0096FF]">Add Member by Email</h4>
            <button onClick={() => setShowAddForm(false)} className="p-1 hover:bg-[#262626] rounded"><X size={16} className="text-[#9E9E9E]" /></button>
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddMember(); }}
              placeholder="rider@example.com"
              className="flex-1 px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] placeholder:text-[#555]"
              data-testid="add-member-email-input"
              autoFocus
            />
            <button
              onClick={handleAddMember}
              disabled={adding}
              className="px-4 py-2.5 bg-[#0096FF] text-white rounded-md text-sm font-semibold hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50"
              data-testid="add-member-submit-btn"
            >
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
          <p className="text-[#555] text-xs mt-2">The user must already be registered on The Show Master</p>
        </div>
      )}

      {/* Search */}
      {orgMembers.length > 3 && (
        <div className="relative mb-3">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members..."
            className="w-full pl-8 pr-4 py-2 bg-[#161616] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors placeholder:text-[#555]"
            data-testid="member-search-input"
          />
        </div>
      )}

      {orgMembers.length === 0 ? (
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-6 text-center">
          <p className="text-[#9E9E9E] text-sm">No members yet. Add members or riders will join when they enter a show.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-6 text-center">
          <p className="text-[#9E9E9E] text-sm">No members match "{search}"</p>
        </div>
      ) : (
        <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-x-auto">
          <table className="w-full min-w-[400px]">
            <thead>
              <tr className="border-b border-[#262626]">
                <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3 pl-4">Rider</th>
                <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3 hidden sm:table-cell">Email</th>
                <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3">Joined</th>
                <th className="text-right text-xs text-[#9E9E9E] font-semibold p-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((member) => (
                <tr key={member.id} className="border-b border-[#1a1a1a] hover:bg-[#0A0A0A]/50" data-testid={`member-${member.id}`}>
                  <td className="p-3 pl-4">
                    <p className="text-white text-sm font-semibold">{member.rider_name}</p>
                    <p className="text-[#9E9E9E] text-xs sm:hidden">{member.rider_email}</p>
                  </td>
                  <td className="p-3 text-[#9E9E9E] text-sm hidden sm:table-cell">{member.rider_email}</td>
                  <td className="p-3 text-[#9E9E9E] text-xs sm:text-sm">{new Date(member.joined_at).toLocaleDateString()}</td>
                  <td className="p-3 pr-4 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setRemoveConfirm(member); }}
                      className="text-[#FF3B30] text-xs hover:text-[#FF6B6B] transition-colors"
                      data-testid={`remove-member-${member.id}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Remove Confirmation */}
      {removeConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setRemoveConfirm(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="remove-member-modal">
            <h3 className="text-lg font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>Remove Member</h3>
            <p className="text-[#9E9E9E] text-sm mb-6">
              Remove <span className="text-white font-semibold">{removeConfirm.rider_name}</span> ({removeConfirm.rider_email}) from {organization.name}?
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRemoveConfirm(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="remove-member-cancel-btn">Cancel</button>
              <button onClick={handleConfirmRemove} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors" data-testid="remove-member-confirm-btn">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
